// External deps
import { useState, useEffect } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import {
  BsChevronDoubleLeft,
  BsChevronDoubleRight,
  BsChevronLeft,
  BsChevronRight,
  BsCalendar3
} from 'react-icons/bs';
import { IoCalendar } from 'react-icons/io5';
import PropTypes from 'prop-types';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { parseISO } from 'date-fns';
import esLocale from 'date-fns/locale/es';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

// Internal deps
import {
  formatDayName,
  formatDay,
  formatMonthName,
  formatYear,
  formatFullDate,
  formatsEnum
} from '../../../utils/formatUtils';
import 'react-datepicker/dist/react-datepicker.css';
import './InputCalendar.scss';
import colors from '../../../styles/colors.module.scss';
import ComponentWithTooltip from '../../ComponentWithTooltip/ComponentWithTooltip';

registerLocale('es', esLocale);
dayjs.extend(utc);

const borderColorEnum = {
  '#FF0000': { border: 'day-red-light', fill: 'day-red' },
  '#312b2bc7': { border: 'day-dark-light', fill: 'day-dark' },
  '#005aff': { border: null, fill: 'day-blue' }
};

const enumDirection = {
  left: 'Left',
  right: 'Right'
};

const formatColor = (value) => {
  if (value?.color in borderColorEnum) {
    return borderColorEnum[value?.color];
  }

  return null;
};

const getMessageLeftDoubleArrow = (isDaily, isWeekly, typeLine) =>
  (isDaily && `Primer día con línea ${typeLine}`) ||
  (isWeekly && `Primer semana con línea ${typeLine}`) ||
  `Primer mes con línea ${typeLine}`;

const getMessageRightDoubleArrow = (isDaily, isWeekly, typeLine) =>
  (isDaily && `Útimo día con línea ${typeLine}`) ||
  (isWeekly && `Útima semana con línea ${typeLine}`) ||
  `Útimo mes con línea ${typeLine}`;

const getMessageLeftArrow = (isDaily, isWeekly) =>
  (isDaily && 'Día anterior') || (isWeekly && 'Semana anterior') || 'Mes anterior';

const getMessageRightArrow = (isDaily, isWeekly) =>
  (isDaily && 'Día siguiente') || (isWeekly && 'Semana siguiente') || 'Mes siguiente';

const isDisabledByMonth = (isMonthly, rangeDateMonth, rangeDateYear, actualDay, actualYear) =>
  isMonthly ? rangeDateMonth.concat(rangeDateYear) === actualDay.concat(actualYear) : false;

const formatDate = (date, format) => (date ? dayjs(date).format(format) : false);

const getTypeLine = (pendingLineTooltipText) => (pendingLineTooltipText ? 'pendiente' : 'devuelta');

const isDisabledLeft = ({
  minDateFormat,
  isDaily,
  isWeekly,
  isMonthly,
  actualDateFormat,
  initalDayOfWeekFormat,
  actualDay,
  actualYear,
  minDateMonth,
  minDateYear
}) => {
  if (minDateFormat && isDaily) {
    return minDateFormat === actualDateFormat;
  }
  if (minDateFormat && isWeekly) {
    return minDateFormat >= initalDayOfWeekFormat;
  }
  if (minDateFormat && isMonthly) {
    return actualDay.concat(actualYear) < minDateMonth.concat(minDateYear);
  }

  return false;
};

const getClassForMSDS = (dateFormat, today, selectDayFormat, newDate, colored) => {
  if (dateFormat === today && !colored) {
    return 'day-blue-light';
  }
  if (dateFormat === selectDayFormat && !colored) {
    return 'day-blue';
  }
  if (dateFormat === selectDayFormat && colored) {
    return `${colored.fill}`;
  }
  if (colored) {
    return `${colored.border}`;
  }

  return '';
};

const isDisabledRight = ({
  maxDateFormat,
  isDaily,
  isWeekly,
  isMonthly,
  actualDateFormat,
  finalDayOfWeekFormat,
  actualDay,
  actualYear,
  maxDateMonth,
  maxDateYear
}) => {
  if (maxDateFormat && isDaily) {
    return maxDateFormat === actualDateFormat;
  }
  if (maxDateFormat && isWeekly) {
    return maxDateFormat <= finalDayOfWeekFormat;
  }
  if (maxDateFormat && isMonthly) {
    return actualDay.concat(actualYear) > maxDateMonth.concat(maxDateYear);
  }

  return false;
};

const getArrowClass = (disabledByMonthDirectional, disabledDirectional) =>
  disabledByMonthDirectional || disabledDirectional ? 'disabled-arrow' : 'arrow';

const getHandleDayBeforeFunction = (
  disabledByMonthDirectional,
  disabledDirectional,
  handleDayBefore
) => (!disabledByMonthDirectional && !disabledDirectional ? () => handleDayBefore() : () => {});

const getClassForNonMSDS = (dateFormat, today, selectDayFormat, newDate, isPendient) => {
  if (dateFormat === today && !isPendient) {
    return 'day-blue-light';
  }
  if (dateFormat === selectDayFormat && !isPendient) {
    return 'day-blue';
  }
  if (isPendient) {
    if (newDate === selectDayFormat) {
      return 'day-red';
    }

    return 'day-red-light';
  }

  return '';
};

const InputCalendar = ({
  minDate,
  maxDate,
  selectDay,
  handleChangeDate,
  arrayDate,
  onClickNextDay,
  onClickDayBefore,
  onClickFirstDay,
  onClickLastDay,
  firstDayOfWeek,
  lastDayOfWeek,
  view,
  showArrowsTooltip,
  minMaxLikeFirstLastDate,
  pendingLineTooltipText,
  showNotification,
  colorCalendar,
  weeklyReportTable,
  isMSDS,
  extendedView
}) => {
  const initDayFormated =
    selectDay || formatFullDate(new Date(), formatsEnum.FORMAT_DATE_2).concat('T00:00:00');
  const [selectedDay, setSelectedDay] = useState(initDayFormated);
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [isNotificationWindowOpen, setIsNotificationWindowOpen] = useState();

  const isDaily = view === 'daily';
  const isWeekly = view === 'weekly';
  const isMonthly = view === 'monthly';

  const typeLine = getTypeLine(pendingLineTooltipText);

  const messageLeftDoubleArrow = getMessageLeftDoubleArrow(isDaily, isWeekly, typeLine);
  const messageRightDoubleArrow = getMessageRightDoubleArrow(isDaily, isWeekly, typeLine);

  const messageLeftArrow = getMessageLeftArrow(isDaily, isWeekly);
  const messageRightArrow = getMessageRightArrow(isDaily, isWeekly);

  const minDateMonth = dayjs(minDate).format('MM');
  const minDateYear = dayjs(minDate).format('YYYY');

  const maxDateMonth = dayjs(maxDate).format('MM');
  const maxDateYear = dayjs(maxDate).format('YYYY');

  const actualDay = dayjs(selectedDay).format('MM');
  const actualYear = dayjs(selectedDay).format('YYYY');

  const disabledByMonthLeft = isDisabledByMonth(
    isMonthly,
    minDateMonth,
    minDateYear,
    actualDay,
    actualYear
  );

  const disabledByMonthRight = isDisabledByMonth(
    isMonthly,
    maxDateMonth,
    maxDateYear,
    actualDay,
    actualYear
  );
  const minDateFormat = formatDate(minDate, formatsEnum.FORMAT_DATE_2);
  const maxDateFormat = formatDate(maxDate, formatsEnum.FORMAT_DATE_2);
  const actualDateFormat = dayjs(selectedDay).format(formatsEnum.FORMAT_DATE_2);
  const initalDayOfWeek = new Date(firstDayOfWeek);
  const finalDayOfWeek = new Date(lastDayOfWeek);
  const initalDayOfWeekFormat = dayjs(initalDayOfWeek).format(formatsEnum.FORMAT_DATE_2);
  const finalDayOfWeekFormat = dayjs(finalDayOfWeek).format(formatsEnum.FORMAT_DATE_2);

  const disabledLeft = isDisabledLeft({
    minDateFormat,
    isDaily,
    isWeekly,
    isMonthly,
    actualDateFormat,
    initalDayOfWeekFormat,
    actualDay,
    actualYear,
    minDateMonth,
    minDateYear
  });

  const disabledRight = isDisabledRight({
    maxDateFormat,
    isDaily,
    isWeekly,
    isMonthly,
    actualDateFormat,
    finalDayOfWeekFormat,
    actualDay,
    actualYear,
    maxDateMonth,
    maxDateYear
  });

  const openPickerDate = () => {
    setOpenDatePicker((open) => !open);
  };

  const showInitUpperCase = (name) => {
    const firtChar = name.charAt(0).toUpperCase();
    const completeName = name.slice(1);

    return firtChar.concat(completeName);
  };

  const renderTooltip = (message, propsTooltip) => (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Tooltip id="button-floating-tooltip-calendar" {...propsTooltip} show>
      <span>{message}</span>
    </Tooltip>
  );

  const datePickerValues = (date) => {
    setSelectedDay(date);
    setOpenDatePicker(false);
  };

  const filterDates = (arrayDateExisting) =>
    arrayDateExisting.reduce((acc, curr) => {
      const existingDateIndex = acc.findIndex((d) => d.date === curr.date);

      if (existingDateIndex === -1) return [...acc, curr];
      const newAcc = [...acc];

      newAcc[existingDateIndex] = { ...newAcc[existingDateIndex], color: curr.color };

      return newAcc;
    }, []);

  const getClassDay = (date, today, newDate, selectDayFormat, isMSDSValue, arrayDateExist) => {
    const dateFormat = formatFullDate(date, formatsEnum.FORMAT_DATE_1);

    if (isMSDSValue) {
      const filteredDates = filterDates(arrayDateExist);
      const colored = formatColor(
        filteredDates.find(
          (d) => formatFullDate(isMSDSValue ? d?.date : d, formatsEnum.FORMAT_DATE_1) === newDate
        )
      );

      return getClassForMSDS(dateFormat, today, selectDayFormat, newDate, colored);
    }
    const isPendient = arrayDate.some(
      (d) => formatFullDate(d, formatsEnum.FORMAT_DATE_1) === newDate
    );

    return getClassForNonMSDS(dateFormat, today, selectDayFormat, newDate, isPendient);
  };

  const renderDate = (day, date) => {
    const today = formatFullDate(new Date(), formatsEnum.FORMAT_DATE_1);
    const newDate = formatFullDate(date, formatsEnum.FORMAT_DATE_1);
    const selectDayFormat = formatFullDate(selectedDay, formatsEnum.FORMAT_DATE_1);

    const classDay = getClassDay(date, today, newDate, selectDayFormat, isMSDS, arrayDate);

    return <div className={`date-calendar ${classDay}`}>{day}</div>;
  };

  const handleNextDay = () => {
    const next = dayjs(selectedDay);
    let nextDate = '';

    if (isDaily) {
      nextDate = next.add(1, 'day');
    }
    if (isWeekly) {
      nextDate = next.add(1, 'week');
    }
    if (isMonthly) {
      nextDate = next.add(1, 'month');
    }
    setSelectedDay(nextDate);
    onClickNextDay();
  };

  const handleDayBefore = () => {
    const before = dayjs(selectedDay);
    let beforeDate = '';

    if (isDaily) {
      beforeDate = before.subtract(1, 'day');
    }
    if (isWeekly) {
      beforeDate = before.subtract(1, 'week');
    }
    if (isMonthly) {
      beforeDate = before.subtract(1, 'month');
    }
    setSelectedDay(beforeDate);
    onClickDayBefore();
  };

  const showDaily = () => (
    <>
      {showInitUpperCase(formatDayName(selectedDay))}
      {', '}
      {formatDay(selectedDay)}
      {' de '}
      {showInitUpperCase(formatMonthName(selectedDay))}
      {' de '}
      {formatYear(selectedDay)}
    </>
  );

  const subString = (val) => {
    const firstChar = val.substring(0, 1).toUpperCase();
    const tallValue = val.substring(1);

    return firstChar.concat(tallValue);
  };

  const handelFirstDay = () => {
    if (minMaxLikeFirstLastDate) {
      const firstDay = dayjs(minDate);

      setSelectedDay(firstDay);
    } else {
      onClickFirstDay();
    }
  };

  const handelLastDay = () => {
    if (minMaxLikeFirstLastDate) {
      const lastDay = dayjs(maxDate);

      setSelectedDay(lastDay);
    } else {
      onClickLastDay();
    }
  };

  const showWeeklyDate = () => {
    // Get the start day of the week (1...30,31)
    // based on the selected date
    const finalInitWeekDay = new Date(firstDayOfWeek);
    const finalLastWeekDay = new Date(lastDayOfWeek);

    // Map start-of-week date
    const dayFormatInitWeek = dayjs(finalInitWeekDay);
    const initShortDayName = dayFormatInitWeek.format('ddd');
    const initDay = dayFormatInitWeek.format('DD');
    const initMonth = dayFormatInitWeek.format('MM');

    // Map end-of-week date
    const nextWeek = dayjs(finalLastWeekDay);
    const nextShortDayName = nextWeek.format('ddd');
    const nextDay = nextWeek.format('DD');
    const nextMonth = nextWeek.format('MM');

    if (`${initShortDayName}` === 'Invalid Date') return '';

    const date = `${subString(initShortDayName)} ${initDay}/${initMonth}
    al ${subString(nextShortDayName)} ${nextDay}/${nextMonth}`;

    return date;
  };

  const showMonthlyDate = () => {
    const monthlyDate = dayjs(selectedDay);
    const nameMonth = monthlyDate.format('MMMM');
    const yearMonth = monthlyDate.format('YYYY');
    const nameMonthFormat = subString(nameMonth);

    return `${nameMonthFormat} de ${yearMonth}`;
  };

  const showDoubleArrow = (direction, message, onClick) => {
    if (showArrowsTooltip) {
      return (
        <OverlayTrigger
          delay={{
            show: 250,
            hide: 400
          }}
          overlay={(overlayProps) => renderTooltip(message, overlayProps)}
          placement="top"
        >
          <div aria-hidden onClick={onClick}>
            {direction === enumDirection.left ? (
              <BsChevronDoubleLeft className="arrow" size={16} />
            ) : (
              <BsChevronDoubleRight className="arrow" size={16} />
            )}
          </div>
        </OverlayTrigger>
      );
    }

    return (
      <div aria-hidden onClick={onClick}>
        {direction === enumDirection.left ? (
          <BsChevronDoubleLeft className="arrow" size={16} />
        ) : (
          <BsChevronDoubleRight className="arrow" size={16} />
        )}
      </div>
    );
  };

  useEffect(() => {
    handleChangeDate(selectedDay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay]);

  useEffect(() => {
    if (selectDay !== selectedDay) {
      setSelectedDay(selectDay);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectDay]);

  useEffect(() => {
    if (colorCalendar && showNotification) {
      if (colorCalendar === colors.calendarOpenWindow) {
        setIsNotificationWindowOpen(true);
      } else {
        setIsNotificationWindowOpen(false);
      }
    }
  }, [colorCalendar, showNotification, isNotificationWindowOpen]);

  const getDateContainerClass = () => {
    if (extendedView) return null;

    let cls = 'date-container';

    if (isDaily && showNotification) {
      cls += ` d-flex align-items-center ${isNotificationWindowOpen ? 'in' : 'out'}`;
    }

    return cls;
  };

  return (
    <div className="calendar-container">
      {!extendedView && showDoubleArrow(enumDirection.left, messageLeftDoubleArrow, handelFirstDay)}

      {showArrowsTooltip ? (
        <OverlayTrigger
          delay={{
            show: 250,
            hide: 400
          }}
          overlay={(overlayProps) => renderTooltip(messageLeftArrow, overlayProps)}
          placement="top"
        >
          <div
            aria-hidden
            onClick={getHandleDayBeforeFunction(disabledByMonthLeft, disabledLeft, handleDayBefore)}
          >
            <BsChevronLeft className={getArrowClass(disabledByMonthLeft, disabledLeft)} size={16} />
          </div>
        </OverlayTrigger>
      ) : (
        <div
          aria-hidden
          onClick={getHandleDayBeforeFunction(disabledByMonthLeft, disabledLeft, handleDayBefore)}
        >
          <BsChevronLeft className={getArrowClass(disabledByMonthLeft, disabledLeft)} size={16} />
        </div>
      )}

      <div className="calendar-container-content">
        <DatePicker
          format="DD/MM/YYYY"
          id="date-input-calendar"
          locale="es"
          maxDate={parseISO(maxDate)}
          minDate={parseISO(minDate)}
          onChange={datePickerValues}
          onClickOutside={() => setOpenDatePicker(false)}
          open={openDatePicker}
          openToDate={new Date(formatFullDate(selectedDay, formatsEnum.FORMAT_DATE_3))}
          renderDayContents={renderDate}
          showDisabledMonthNavigation
          size="small"
          todayButton="Hoy"
        />
        <div
          aria-hidden
          className={getDateContainerClass()}
          id="calendar-div"
          onClick={() => openPickerDate()}
          style={{ minWidth: weeklyReportTable ? '15px' : '' }}
        >
          {isDaily && showNotification ? (
            <div className={`calendar-notification ${isNotificationWindowOpen ? 'in' : 'out'}`}>
              <BsCalendar3 />
            </div>
          ) : null}
          {isDaily && showDaily()}

          {isWeekly && extendedView ? (
            <div className="position-relative">
              <ComponentWithTooltip tooltipText={showWeeklyDate()}>
                <IoCalendar className="calendar-icon" size={22} />
              </ComponentWithTooltip>
            </div>
          ) : (
            isWeekly && showWeeklyDate()
          )}

          {isMonthly && showMonthlyDate()}
        </div>
      </div>
      {showArrowsTooltip ? (
        <OverlayTrigger
          delay={{
            show: 250,
            hide: 400
          }}
          overlay={(overlayProps) => renderTooltip(messageRightArrow, overlayProps)}
          placement="top"
        >
          <div
            aria-hidden
            onClick={getHandleDayBeforeFunction(disabledByMonthRight, disabledRight, handleNextDay)}
          >
            <BsChevronRight
              className={getArrowClass(disabledByMonthRight, disabledRight)}
              size={16}
            />
          </div>
        </OverlayTrigger>
      ) : (
        <div
          aria-hidden
          onClick={getHandleDayBeforeFunction(disabledByMonthRight, disabledRight, handleNextDay)}
        >
          <BsChevronRight
            className={getArrowClass(disabledByMonthRight, disabledRight)}
            size={16}
          />
        </div>
      )}
      {!extendedView &&
        showDoubleArrow(enumDirection.right, messageRightDoubleArrow, handelLastDay)}
    </div>
  );
};

InputCalendar.propTypes = {
  minDate: PropTypes.string,
  maxDate: PropTypes.string,
  selectDay: PropTypes.string,
  firstDayOfWeek: PropTypes.string,
  lastDayOfWeek: PropTypes.string,
  handleChangeDate: PropTypes.func,
  arrayDate: PropTypes.arrayOf(
    PropTypes.shape({
      color: PropTypes.string,
      date: PropTypes.string
    })
  ),
  onClickNextDay: PropTypes.func,
  onClickDayBefore: PropTypes.func,
  onClickFirstDay: PropTypes.func,
  onClickLastDay: PropTypes.func,
  view: PropTypes.oneOf(['daily', 'weekly', 'monthly']),
  showArrowsTooltip: PropTypes.bool,
  minMaxLikeFirstLastDate: PropTypes.bool,
  pendingLineTooltipText: PropTypes.bool,
  showNotification: PropTypes.bool,
  colorCalendar: PropTypes.string,
  weeklyReportTable: PropTypes.bool,
  isMSDS: PropTypes.bool,
  extendedView: PropTypes.bool
};

InputCalendar.defaultProps = {
  minDate: '',
  maxDate: '',
  selectDay: '',
  firstDayOfWeek: '',
  lastDayOfWeek: '',
  handleChangeDate: () => {
    /* empty */
  },
  arrayDate: [],
  onClickNextDay: () => {
    /* empty */
  },
  onClickDayBefore: () => {
    /* empty */
  },
  onClickFirstDay: () => {
    /* empty */
  },
  onClickLastDay: () => {
    /* empty */
  },
  view: 'daily',
  showArrowsTooltip: true,
  minMaxLikeFirstLastDate: false,
  pendingLineTooltipText: false,
  showNotification: false,
  colorCalendar: '#000000',
  weeklyReportTable: false,
  isMSDS: false,
  extendedView: false
};

export default InputCalendar;
